import { jsPDF } from "jspdf";
import _ from "lodash";
import { CategoryData, CategoryType } from "src/revamp/types/PatientFormTypes";
import { User } from "src/services/dto/Security";

import {
  PersonalSleepHistoryOptions,
  epworthSleepinessScaleOptions,
  socialStatusesData,
  specificMedicalProblemsData,
} from "../../constants/PatientFormConstants";
import { Medication } from "../../types/MedicationTypes";
import { calculateBMI, formatPhoneNumber, getName } from "../helpers";
import { PlexFont, PoppinNormal } from "./constants";
import {
  PDFGeneratorState,
  drawFooter,
  drawHeader,
  drawProvider,
  drawRow,
  drawSectionTitle,
  drawTitle,
  drawValue,
  endSection,
  formatDate,
  getMedicationDosage,
  getMedicationName,
  getValue,
} from "./helper";

const drawPatientInfo = (state: PDFGeneratorState) => {
  drawRow(state, "Name:", getName(state.patient));
  drawRow(state, "Date of Birth:", state.patient.dateOfBirth);
  drawRow(state, "Gender:", state.patient.gender);
  drawRow(state, "Phone:", formatPhoneNumber(state.patient.phone));
  drawRow(state, "Email:", state.patient.email);
  endSection(state);
};

const drawObjective = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Objective:");
  const reviewOfSystems = getValue(state, "reviewOfSystems");
  const height = reviewOfSystems.height;
  const weight = reviewOfSystems.weight;
  const unit = reviewOfSystems.unit;

  const heightStr =
    unit === "Imperial" ? `${height.feet || 0}'${height.inches || 0}"` : `${height.cm || 0} cm`;

  const weightStr = unit === "Imperial" ? `${weight.pounds || 0} lbs` : `${weight.kg || 0} kg`;

  const bmi = calculateBMI({ height, weight, unit });

  drawValue(state, "Height:", heightStr);
  drawValue(state, "Weight:", weightStr);
  drawValue(state, "BMI:", bmi.toFixed(1));

  drawValue(state, "Recent Symptoms:");
  const recentSymptoms = getValue(state, "reviewOfSystems.recentSymptoms") || [];
  if (_.isEmpty(recentSymptoms)) {
    drawValue(state, "No recent symptoms reported");
  } else {
    recentSymptoms.forEach((symptom: string) => drawValue(state, `- ${symptom}`));
  }

  endSection(state);
};

const drawMedications = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Medications:");
  const medications = getValue(state, "medications.activeMedications") || [];

  if (_.isEmpty(medications)) {
    drawValue(state, "No medications reported");
  } else {
    drawValue(state, "The patient is currently taking the following medications:");
    medications.forEach((med: Medication) => {
      const name = getMedicationName(med);
      const dosage = getMedicationDosage(med);
      const frequency = med.frequency || "";
      drawValue(state, `- ${name}: ${dosage} ${frequency}`);
    });
  }
  endSection(state);
};

const drawAllergies = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Allergies:");
  const allergies = getValue(state, "medications.medicationAllergies") || [];

  if (_.isEmpty(allergies)) {
    drawValue(state, "No allergies reported");
  } else {
    drawValue(state, "The patient is allergic to the following medications:");
    allergies.forEach((allergy: any) => {
      drawValue(state, `- ${allergy.name}`);
    });
  }

  endSection(state);
};

const drawPastMedicalHistory = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Past Medical History:");
  const pmh = getValue(state, "pastMedicalHistory");

  if (pmh?.hasPaceMaker) {
    drawValue(state, "The patient has a pacemaker");
  }

  const generalProblems = pmh?.generalMedicalProblems || {};

  if (!_.isEmpty(generalProblems)) {
    drawValue(state, "The patient reports having the following conditions:");
    generalProblems.forEach((condition: any) => {
      drawValue(
        state,
        `- ${condition.name}${condition.description ? `: ${condition.description}` : ""}`,
      );
    });
  }

  const specificProblems = pmh?.specificMedicalProblems || {};

  if (!_.isEmpty(specificProblems)) {
    drawValue(state, "The patient reports having care for the following:");
    specificProblems.forEach((item: any) => {
      let value = item.name;
      if (item.description) {
        value += `: ${item.description}`;
      }
      if (item.type) {
        value += ` (${item.type})`;
      }
      drawValue(state, `- ${value}`);
    });
  }
  endSection(state);
};

const drawSocialHistory = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Social History:");
  const social = getValue(state, "socialHistory");

  if (social) {
    drawValue(state, "Marital Status:", social.maritalStatus);

    const socialStatuses = social.socialStatuses || {};
    socialStatusesData.forEach((item) => {
      const index = socialStatuses.findIndex((status: any) => status.name === item.value);
      if (index === -1) {
        drawValue(state, `- ${_.upperFirst(item.value)}: No`);
      } else {
        drawValue(
          state,
          `- ${_.upperFirst(item.value)}: Yes, ${socialStatuses[index].description}`,
        );
      }
    });
  }
  endSection(state);
};

const drawFamilyHistory = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Family History:");
  const family = getValue(state, "familyHistory");

  if (family?.badSleep?.response) {
    drawValue(state, "The patient reports having family members with sleep disorders.");
    if (family?.badSleep?.description) {
      drawValue(state, `- Description: ${family.badSleep.description}`);
    }
  }

  const conditions = family?.familyMedicalConditions || [];
  conditions.forEach((condition: any) => {
    drawValue(state, `${condition.relationship}`);
    drawValue(state, `- Deceased: ${condition.deceased}`);
    if (condition.age) {
      drawValue(state, `- Age: ${condition.age}`);
    }
    if (condition.description) {
      drawValue(state, `- Description: ${condition.description}`);
    }
  });
  endSection(state);
};

const drawSleepMedicineHistory = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Sleep Medicine History:");
  const sleep = getValue(state, "sleepMedicineHistory");

  if (sleep) {
    drawValue(state, "Past Sleep Study:", sleep.pastSleepStudy?.response ? "Yes" : "No");
    drawValue(
      state,
      "Currently On CPAP/BiPAP:",
      sleep.isOnPAP ? `Yes${sleep.papVendor ? ` (${sleep.papVendor})` : ""}` : "No",
    );
    drawValue(
      state,
      "Ever had NEAR ACCIDENT or ACCIDENT due to excessive drowsiness:",
      sleep.drowsinessAccident?.response ? "Yes" : "No",
    );

    const questionnaire = sleep.sleepQuestionnaire || {};
    drawValue(
      state,
      "Average hours of sleep per night:",
      `${questionnaire.averageHoursSlept || 0} hours`,
    );
    drawValue(state, "Average Bedtime:", `${questionnaire.averageBedtime}`);
    drawValue(state, "Average Rise Time:", `${questionnaire.averageRiseTime}`);
    drawValue(
      state,
      "Average time it takes to fall asleep once in bed:",
      `${questionnaire.averageMinutesToDoze || 0} minutes`,
    );
    drawValue(state, "Number of Awakenings:", `${questionnaire.numberOfAwakenings || 0}`);

    const reports = PersonalSleepHistoryOptions.filter((option) => questionnaire[option.value]).map(
      (option) => option.label,
    );

    if (reports.length > 0) {
      drawValue(state, "Patient reports the following:");
      reports.forEach((report) => {
        drawValue(state, `- ${report}`);
      });
    }
  }
  endSection(state);
};

const drawEpworthSleepinessScale = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Epworth Sleep Score:");
  const epworth = getValue(state, "sleepMedicineHistory.epworthSleepinessScale");

  if (epworth) {
    drawValue(state, "How likely is the patient to doze off in the situations below:");
    for (const option of epworthSleepinessScaleOptions) {
      const value = epworth[option.name];
      if (value) {
        drawValue(state, `- ${option.label}:`, value as string);
      }
    }
    drawValue(state, "SCORE:", epworth.score.toString());
  }
  endSection(state);
};

const drawWeightHealth = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Weight Health:");
  const weight = getValue(state, "weightHealth");

  drawValue(state, "Bothered by weight:", weight.botheredByWeight);
  drawValue(state, "Want to discuss weight:", weight.discussWeight ? "Yes" : "No");
  if (!_.isEmpty(weight.weightLossMethods)) {
    drawValue(state, "Weight loss methods:", weight.weightLossMethods?.join(", ") || "None");
  }
  if (!_.isNil(weight.eatingDisorder)) {
    drawValue(state, "Eating disorder:", weight.eatingDisorder ? "Yes" : "No");
  }
  if (!_.isNil(weight.pregnant)) {
    drawValue(state, "Pregnant:", weight.pregnant ? "Yes" : "No");
  }
  if (!_.isNil(weight.pregnantPlanning)) {
    drawValue(state, "Pregnant planning:", weight.pregnantPlanning ? "Yes" : "No");
  }

  endSection(state);
};

const drawProviderHistory = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Provider History:");
  const providers = getValue(state, "providerHistory");

  if (providers?.primaryCareProvider) {
    drawProvider(state, providers.primaryCareProvider);
  }

  if (providers?.hasReferringProvider) {
    if (!providers?.isSameAsPrimaryCare && providers?.referringProvider) {
      endSection(state);
      drawProvider(state, providers.referringProvider);
    } else if (providers?.isSameAsPrimaryCare) {
      drawValue(state, "*The patient's primary care provider is also their referring provider.");
    }
  }

  endSection(state);
};

const drawAccidentInfo = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Accident Related:");
  const accident = getValue(state, "reviewOfSystems.accidentRelated");

  if (accident?.accident && accident?.accident === "No") {
    drawValue(state, "This visit is not related to an automobile or work accident");
  } else {
    drawValue(state, `This visit is related to a ${accident?.accident.toLowerCase()}`);
    if (accident?.date) {
      drawValue(state, `Date: ${formatDate(accident.date)}`);
    }
    if (accident?.accidentInsurance) {
      drawValue(state, `Insurance: ${accident.accidentInsurance}`);
    }
    if (accident?.employer) {
      drawValue(state, `Employer: ${accident.employer}`);
    }
  }

  endSection(state);
};

// Main generator function
export const generateNewPatientPDF = (
  patient: User,
  formData: Record<CategoryType, CategoryData>,
): jsPDF => {
  // Initialize state
  const state: PDFGeneratorState = {
    doc: new jsPDF("p", "mm", "letter"),
    y: 12,
    patient,
    formData,
  };

  // Setup fonts
  state.doc.addFileToVFS("Poppins-SemiBold.ttf", PlexFont);
  state.doc.addFont("Poppins-SemiBold.ttf", "PoppinsBold", "normal");
  state.doc.addFileToVFS("Poppins-Regular.ttf", PoppinNormal);
  state.doc.addFont("Poppins-Regular.ttf", "PoppinsNormal", "normal");

  // Draw content
  drawHeader(state);
  drawTitle(state, "New Patient Forms");

  drawPatientInfo(state);
  drawObjective(state);
  drawMedications(state);
  drawAllergies(state);
  drawPastMedicalHistory(state);
  drawSocialHistory(state);
  drawFamilyHistory(state);
  drawSleepMedicineHistory(state);
  drawEpworthSleepinessScale(state);
  drawWeightHealth(state);
  drawProviderHistory(state);
  drawAccidentInfo(state);

  drawFooter(state);

  return state.doc;
};
